import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { updateTeamPage, updateTeamPhoto, updateTeam } from '../../../helpers/requests/fundraising-pages';
import {
  useTeamCaptainSurveyResponses,
  useTeamInfo,
  useTeamPageInfo,
  useTeamPhoto,
  useTeamRoster,
  useTeamMemberPhotos,
  useSurveyResponses,
  useBackgroundImage,
} from '../../../hooks';
import ProgressBar from '../../../components/progress-bar';
import PageTitle from '../../../components/page-title';
import TopDiyList from '../../../components/top-diy-list';
import TopList from '../../../components/top-list';
import ProfileImage from '../../../components/profile-image';
import SocialShare from '../../../components/social-share';
import { convertCentsToDollars } from '../../../helpers/base';
import EventDetailsRow from '../../../components/event-details-row';

import MobileSocialShareModal from '../../../components/mobile-social-share-modal';
import EditGoalModal from '../../../components/edit-goal-modal';
import EditImageModal from '../../../components/edit-image-modal';
import EditRichTextModal from '../../../components/edit-rich-text-modal';
import EditTitleModal from '../../../components/edit-title-modal';
import LinkListModal from '../../../components/link-list-modal';
import { EVENT_TYPE } from '../../../constants/dom-events';
import ReactHtmlParser from 'react-html-parser';
import EditBackgroundImageModal from '../../../components/edit-background-image-modal';
import { updateSurveyResponses, updateSurveyBackgroundResponses } from '../../../services/luminate';
import SearchParticipant from '../../../components/search-participant';
import EditEventDetailsModal from '../../../components/edit-event-details-modal';

/** Page Constants */
const EVENT_ID = luminateProperties.teamPage.frId;
const TEAM_ID = luminateProperties.teamPage.teamId;
const EVENT_TYPE_SEARCH = 'celebrate';
const DEFAULT_BG_IMAGE = '../images/content/pagebuilder/diy-registration-background-lrg.png';

//Teamraiser info
const ACCEPTING_REGISTRATIONS_GIFTS = luminateProperties.teamraiserStatus === '2' ? true : false;

document.addEventListener(EVENT_TYPE.DOM_LOAD, () => {
  const $layout = document.querySelector('#appRoot');
  ReactDOM.render(<TeamPage />, $layout);
});

const TeamPage = () => {
  const teamInfo = useTeamInfo(TEAM_ID);
  const teamPageInfo = useTeamPageInfo(EVENT_ID, TEAM_ID);
  const teamPhoto = useTeamPhoto(EVENT_ID, teamInfo.data.captainConsId);
  const teamRoster = useTeamRoster(EVENT_ID, TEAM_ID);
  const surveyResponses = useTeamCaptainSurveyResponses(EVENT_ID, teamInfo.data.captainConsId);
  const surveyResponsesForEventDetailsUpdate = useSurveyResponses(EVENT_ID, teamInfo.data.captainConsId);
  const teamMemberPhotos = useTeamMemberPhotos(EVENT_ID, teamRoster.data);
  const surveyResponsesForBgImage = useSurveyResponses(EVENT_ID, teamInfo.data.captainConsId);
  const bgImageData = useBackgroundImage(EVENT_ID);
  const teamNameSearch = encodeURIComponent(teamInfo.data.name);
  const viewAllUrl = `${luminateProperties.secure}SPageServer?pagename=${EVENT_TYPE_SEARCH}_search&eventId=${EVENT_ID}&searchType=participant&filter=all&groupName=${teamNameSearch}`;
  const joinTeamUrl = `${luminateProperties.secure}SPageServer?pagename=${EVENT_TYPE_SEARCH}_register&fr_id=${EVENT_ID}&fr_rtype=join&fr_tjoin=${TEAM_ID}`;

  const IS_TEAM_CAPTAIN = luminateProperties.user.consId && parseInt(luminateProperties.user.consId) === parseInt(teamInfo.data.captainConsId);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [editGoalModalIsOpen, setEditGoalModalIsOpen] = useState(false);
  const [editTitleModalIsOpen, setEditTitleModalIsOpen] = useState(false);
  const [editPhotoModalIsOpen, setEditPhotoModalIsOpen] = useState(false);
  const [editBackgroundPhotoModalIsOpen, setEditBackgroundPhotoModalIsOpen] = useState(false);
  const [editRichTextModalIsOpen, setEditRichTextModalIsOpen] = useState(false);
  const [editEventModalIsOpen, setEditEventModalIsOpen] = useState(false);
  const [supportTeamModalIsOpen, setSupportTeamModalIsOpen] = useState(false);
  const [mobileSocialShareModalIsOpen, setMobileSocialShareModalIsOpen] = useState(false);
  const [selectedBackgroundImage, setSelectedBackgroundImage] = useState(DEFAULT_BG_IMAGE);

  /** value initializations */
  //let hasEventDetails = false;

  const closeModals = () => {
    setEditGoalModalIsOpen(false);
    setEditTitleModalIsOpen(false);
    setEditPhotoModalIsOpen(false);
    setEditBackgroundPhotoModalIsOpen(false);
    setEditRichTextModalIsOpen(false);
    setSupportTeamModalIsOpen(false);
    setMobileSocialShareModalIsOpen(false);
    setErrorMessage(null);
  };

  const renderHeroImage = () => {
    let bgImg = DEFAULT_BG_IMAGE;

    if (surveyResponsesForBgImage.data) {
      const responses = surveyResponsesForBgImage.data.surveyResponses;
      if (responses && Array.isArray(responses)) {
        responses.map(response => {
          if (response.key === 'TEAM_BACKGROUND' && !(response.responseValue instanceof Object)) {
            bgImg = response.responseValue;
          }
        });
      }
    }

    return (
      <div className="hero has-page-covering" style={{ backgroundImage: `url('${bgImg}')` }}>
        <div className="has-text-right" data-container="background-image">
          {renderEditButton({
            text: 'Edit Photo',
            action: () => {
              setEditBackgroundPhotoModalIsOpen(true);
              setSelectedBackgroundImage(bgImg);
            },
          })}
        </div>
      </div>
    );
  };

  const renderProgressBar = () => (
    <ProgressBar
      goal={teamInfo.data.goal}
      progress={teamInfo.data.amountRaised}
      showIndicator={true}
      goalText="Our goal:"
      raisedText="Amount Raised:"
      editButton={renderEditButton({ text: 'Edit Goal', action: () => setEditGoalModalIsOpen(true), className: 'edit-goal' })}
    />
  );

  const renderTopParticipants = () => {
    if (teamMemberPhotos.data) {
      return <TopDiyList items={teamMemberPhotos.data} showLink={true} eventId={EVENT_ID} />;
    }
  };

  const renderTitle = () => {
    //luminate only returns a max of 25 (default) so this number will only be accurate up to 25. Need to use the numMembers supplied in useTeamInfo
    //const participantCount = teamRoster.data.length;
    const participantCount = teamInfo.data.numMembers;

    const renderGroupLeaderLink = () =>
      `<a href="TR?pg=personal&fr_id=${EVENT_ID}&px=${teamInfo.data.captainConsId}" aria-label="Vist group leader ${teamInfo.data.captainFirstName} ${teamInfo.data.captainLastName}'s fundraising page">${teamInfo.data.captainFirstName} ${teamInfo.data.captainLastName}</a>`;

    return (
      <PageTitle
        title={teamInfo.data.name}
        firstSubtitle={`${participantCount} Member${participantCount === 1 ? '' : 's'}`}
        secondSubtitle={`Group Leader: ${renderGroupLeaderLink()}`}
      />
    );
  };

  const fundraisingLeaderboardRef = React.createRef();

  const scrollToAnchorLink = ref => {
    const el = ref.current ? ref.current : ref;

    el.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const renderDonationButtons = () => (
    <div className="donate-buttons">
      <button onClick={() => scrollToAnchorLink(fundraisingLeaderboardRef)} className="button is-donate">
        Donate to a Group Member
      </button>
      {ACCEPTING_REGISTRATIONS_GIFTS && (
        <a href={`${joinTeamUrl}`} target="_blank" rel="noreferrer" className="button is-primary">
          Fundraise with this Group
        </a>
      )}
      <div>
        <a href={`${teamInfo.data.teamDonateURL}`}>Make a general donation to this fundraiser</a>
      </div>
    </div>
  );

  const renderProfileImage = () => <ProfileImage src={teamPhoto.data.customUrl} />;

  const renderSocialShare = () => <SocialShare title={'Share'} description={'Help support the kids of St. Jude'} image={'../images/events/socialevents.jpg'} />;

  const renderSupportTeamModal = () => {
    if (teamRoster.data.length > 0) {
      const items = teamRoster.data
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({
          linkText: item.name,
          linkHref: item.donationLink,
        }));
      return <LinkListModal isOpen={supportTeamModalIsOpen} onClose={closeModals} title="Support a Project" items={items} />;
    }
    return null;
  };

  const renderChangeGoalModal = () => (
    <EditGoalModal
      isOpen={editGoalModalIsOpen}
      onClose={closeModals}
      goal={convertCentsToDollars(teamInfo.data.goal)}
      handleSubmit={handleUpdateGoal}
      errorMessage={errorMessage}
    />
  );

  const renderChangeTitleModal = () => (
    <EditTitleModal
      isOpen={editTitleModalIsOpen}
      onClose={closeModals}
      title={teamInfo.data.name}
      handleSubmit={handleUpdateTitle}
      errorMessage={errorMessage}
    />
  );

  const renderChangePhotoModal = () => (
    <EditImageModal
      isOpen={editPhotoModalIsOpen}
      onClose={closeModals}
      image={teamPhoto.data.customUrl}
      isLoading={isLoading}
      handleSubmit={handleUpdatePhoto}
      errorMessage={errorMessage}
    />
  );

  const renderChangeBackgroundPhotoModal = () => {
    if (bgImageData.data && bgImageData.data.images) {
      const imgData = bgImageData.data.images;
      return (
        <EditBackgroundImageModal
          isOpen={editBackgroundPhotoModalIsOpen}
          onClose={closeModals}
          image={selectedBackgroundImage}
          isLoading={isLoading}
          handleSubmit={handleUpdateBackgroundPhoto}
          errorMessage={errorMessage}
          bgImageData={imgData}
        />
      );
    }
  };

  const renderChangeRichTextModal = () => (
    <EditRichTextModal
      isOpen={editRichTextModalIsOpen}
      onClose={closeModals}
      richText={teamPageInfo.data.richText}
      handleSubmit={handleUpdateRichText}
      errorMessage={errorMessage}
    />
  );

  const renderEditButton = ({ text, action, className }) => {
    if (teamInfo.data.captainConsId === parseInt(luminateProperties.user.consId)) {
      return (
        <div className={`edit-container ${className ? className : ''}`} data-container="edit-container">
          <button className="button is-link is-small is-narrow" onClick={action}>
            <span className="icon is-small">
              <i className="svg-icon edit-icon is-white"></i>
            </span>
            <span>{text}</span>
          </button>
        </div>
      );
    } else {
      return null;
    }
  };

  const renderMobileSocialShareButton = ({ action }) => <button onClick={action}>Share</button>;

  const renderMobileSocialShareModal = () => (
    <MobileSocialShareModal isOpen={mobileSocialShareModalIsOpen} onClose={closeModals} socialShare={renderSocialShare()} />
  );

  const renderEventDetails = () => {
    const personalEvent = surveyResponses.data;
    const eventDetails = [];

    if (personalEvent) {
      if (
        !personalEvent.venue &&
        !personalEvent.address1 &&
        !personalEvent.address2 &&
        !personalEvent.startDate &&
        !personalEvent.endDate &&
        !personalEvent.startTime &&
        !personalEvent.endTime
      ) {
        //hasEventDetails = false;
        return false;
      }

      //hasEventDetails = true;

      Object.keys(personalEvent).forEach(data => {
        personalEvent[data] = personalEvent[data] && personalEvent[data].includes('User Provided No Response') ? '' : personalEvent[data];
      });

      const setStartEndInfo = (start, end) => `${start} ${end ? ' - ' + end : ''}`;
      const setActionInfo = (icon, subText, link) => ({ icon, subText, link });
      const setValueOrNone = value => (value ? value : '');
      const setEventGeo = event => `${event.city ? event.city : ''} ${event.state ? ', ' + event.state : ''} ${event.postalCode ? event.postalCode : ''}`;
      const setEventDetails = (array, textRows, actionButton) => {
        array.push({ textRows, actionButton });
      };
      const getGoogleMapLinkFromAddress = event => {
        let address = `${event.venue || ''}`;
        address += `${event.address1 || ''} ${event.address2 || ''}`;
        address += ` ${event.city || ''} ${event.state ? ', ' + event.state : ''} ${event.postalCode || ''}`;
        return `https://www.google.com/maps/search/?api=1&query=${encodeURI(address)}`;
      };

      const eventDetailsContent = [
        {
          text: [setStartEndInfo(personalEvent.startDate, personalEvent.endDate), setStartEndInfo(personalEvent.startTime, personalEvent.endTime)],
          action: setActionInfo('calendar', '', 'no-link'),
        },
        {
          text: [
            setValueOrNone(personalEvent.venue),
            setValueOrNone(personalEvent.address1),
            setValueOrNone(personalEvent.address2),
            setEventGeo(personalEvent),
          ],
          action: setActionInfo('marker', 'MAP', getGoogleMapLinkFromAddress(personalEvent)),
        },
      ];

      eventDetailsContent.map(row => setEventDetails(eventDetails, row.text, row.action));

      return (
        <div data-container="event-details">
          <h3 className="has-text-white">
            Event Details
            {renderEditButton({
              text: 'Edit Event',
              action: () => setEditEventModalIsOpen(true),
            })}
          </h3>
          <EventDetailsRow eventDetails={eventDetails} />
        </div>
      );
    }
  };

  const renderTopDonors = () => {
    const items = [];
    const trDonorList = document.querySelector('.vscroll-container div');

    if (trDonorList) {
      Object.keys(trDonorList.children).forEach(listRow => {
        const html = trDonorList.children[listRow].innerHTML;
        if (html) {
          const htmlSplit = html.split('</div>');
          if (htmlSplit.length > 1) {
            let donorName = htmlSplit[0].replace('<div class="team-honor-list-name">', '').trim();
            donorName = donorName.replace('&amp;', `&`);
            const amount = htmlSplit[1].replace('<div class="team-honor-list-value">', '').trim();

            items.push({
              title: `${donorName}`,
              subtitle: amount,
            });
          }
        }
      });

      if (items.length > 0) {
        return <TopList {...{ items }} />;
      } else {
        return <p>No donations yet.</p>;
      }
    }
  };
  
  const renderSidebar = () => (
    <div>
      <div className="dark-gradient-bg">
        {renderEventDetails()}
        <hr />
        <div data-tab-content="donors">
          <h3 className="has-text-white">Donors</h3>
          <div data-container="donors">{renderTopDonors()}</div>
        </div>
      </div>
    </div>
  );

  const renderChangeEventModal = () => {
    if (surveyResponsesForEventDetailsUpdate.data && surveyResponsesForEventDetailsUpdate.data.surveyResponses && IS_TEAM_CAPTAIN) {
      const eventDetailsResponses = [];
      surveyResponsesForEventDetailsUpdate.data.surveyResponses.forEach((response, i) => {
        if (i < 11) {
          eventDetailsResponses.push({
            key: response.key,
            value: typeof response.responseValue !== 'object' ? response.responseValue : '',
            questionText: response.questionText,
            type: response.questionType,
          });
        }
      });

      return (
        <EditEventDetailsModal
          isOpen={editEventModalIsOpen}
          onClose={closeModals}
          surveyData={eventDetailsResponses}
          handleSubmit={handleUpdateEventDetails}
          isLoading={isLoading}
          errorMessage={errorMessage}
        />
      );
    }
  };

  console.log(`Team captain: ${IS_TEAM_CAPTAIN}`);

  const handleUpdateGoal = async values => {
    const response = await updateTeam({ eventId: EVENT_ID, teamGoal: values.goal * 100 });
    if (response.success) {
      teamInfo.reload();
      setEditGoalModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  const handleUpdatePhoto = async blob => {
    setIsLoading(true);
    const response = await updateTeamPhoto({ eventId: EVENT_ID, photo: blob });
    setIsLoading(false);
    if (response.success) {
      teamPhoto.reload();
      setEditPhotoModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  const handleUpdateBackgroundPhoto = async bgImage => {
    setIsLoading(true);
    const responses = [
      {
        type: 'string',
        key: 'question_key_TEAM_BACKGROUND',
        value: bgImage,
      },
    ];
    const response = await updateSurveyBackgroundResponses(EVENT_ID, responses);
    setIsLoading(false);

    if (response[0].success) {
      surveyResponsesForBgImage.reload();
      setEditBackgroundPhotoModalIsOpen(false);
      luminateProperties.personalPage.personalEvent.backgroundImage = bgImage;
    } else {
      setErrorMessage(response[0].message);
    }
  };

  const handleUpdateRichText = async values => {
    const response = await updateTeamPage({ eventId: EVENT_ID, richText: values.richText });
    if (response.success) {
      teamPageInfo.reload();
      setEditRichTextModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  const handleUpdateTitle = async values => {
    const response = await updateTeam({ eventId: EVENT_ID, teamName: values.title });
    if (response.success) {
      teamInfo.reload();
      setEditTitleModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  const handleUpdateEventDetails = async (values, surveyData) => {
    setIsLoading(true);
    const responses = [];
    surveyData.map(data => {
      Object.keys(values).map(value => {
        if (data.key === value) {
          responses.push({
            key: `question_key_${data.key}`,
            type: data.type,
            value: values[value],
          });
        }
      });
    });
    const response = await updateSurveyResponses(EVENT_ID, responses);
    setIsLoading(false);

    if (response[0].success) {
      await surveyResponses.reload();
      setEditEventModalIsOpen(false);
    } else {
      setErrorMessage(response[0].message);
    }
  };

  return (
    <div className="diy-team">
      {renderChangeGoalModal()}
      {renderChangeTitleModal()}
      {renderChangePhotoModal()}
      {renderChangeBackgroundPhotoModal()}
      {renderChangeRichTextModal()}
      {renderSupportTeamModal()}
      {renderMobileSocialShareModal()}
      {renderChangeEventModal()}

      {renderHeroImage()}
      <section className="section">
        <div className="container">
          <div className="columns">
            {/* left column */}
            <div className="column is-one-quarter-desktop sidebar-section is-hidden-mobile is-hidden-tablet-only">
              <div className="has-text-right" data-container="profile-image">
                {renderEditButton({
                  text: 'Edit Photo',
                  action: () => setEditPhotoModalIsOpen(true),
                  className: 'edit-container',
                })}
                {renderProfileImage()}
              </div>
              {renderSidebar()}
            </div>

            {/* right column */}
            <div className="column is-three-quarter-desktop main-content-section">
              <div className="has-text-right is-hidden-desktop" data-container="profile-image">
                <div className="is-hidden-desktop mobile-social-share-button">
                  {renderMobileSocialShareButton({
                    action: () => setMobileSocialShareModalIsOpen(true),
                  })}
                </div>
                {renderEditButton({
                  text: 'Edit Photo',
                  action: () => setEditPhotoModalIsOpen(true),
                })}
                {renderProfileImage()}
              </div>
              <div className="title-and-progress-container">
                <div data-container="title">
                  <div className="level">
                    <div className="level-left">{renderTitle()}</div>
                    <div className="level-right is-hidden-mobile is-hidden-tablet-only">
                      <div data-container="social-share" className="columns is-vcentered">
                        <p className="has-text-weight-bold column is-narrow">Share:</p>
                        {renderSocialShare()}
                      </div>
                    </div>
                  </div>
                </div>
                <div data-container="progress">{renderProgressBar()}</div>
                {renderDonationButtons()}
              </div>
              {teamRoster.data.length > 0 && (
                <section className="section global-leaderboards fundraising-leaderboard-section pt-3 pb-3" data-container="donors">
                  <div className="container leaderboard-wrap">
                    <div className="tab-content-wrap">
                      <div className="columns">
                        <section className="column is-full tab-content is-active">
                          <div className="columns mb-0">
                            <div className="column is-full">
                              <h3>Fundraising Leaderboard</h3>
                            </div>
                          </div>
                          <div className="columns mb-0">
                            <div className="column is-full pb-o">
                              <SearchParticipant
                                eventId={EVENT_ID}
                                label={'Find Someone in this Group'}
                                teamName={teamInfo.data.name}
                                eventType={EVENT_TYPE_SEARCH}
                              />
                            </div>
                          </div>
                          <div className="columns">
                            <div className="column is-full py-0">
                              <p className="fundraising-leaderboard-counter">
                                {teamMemberPhotos.data.length > 4
                                  ? `Viewing 1-5 of ${teamMemberPhotos.data.length}`
                                  : `Viewing ${teamMemberPhotos.data.length} of ${teamMemberPhotos.data.length}`}
                              </p>
                            </div>
                          </div>
                          <div className="columns mb-0 is-hidden-mobile">
                            <div className="column">
                              <p className="has-text-weight-bold mb-0">Fundraiser Name</p>
                            </div>
                            <div className="column">
                              <div className="columns mb-0 is-flex">
                                <div className="column">
                                  <p className="has-text-weight-bold has-text-right mb-0">Amount Raised</p>
                                </div>
                                <div className="column"></div>
                              </div>
                            </div>
                          </div>
                          <div className="columns" ref={fundraisingLeaderboardRef}>
                            <div className="column is-full">{teamMemberPhotos.data.length > 0 && renderTopParticipants()}</div>
                          </div>
                          <div className="columns">
                            <div className="column is-full has-text-centered">
                              {teamMemberPhotos.data.length > 0 && (
                                <a href={viewAllUrl} className="button is-outline mb-4">
                                  See All Group Members
                                </a>
                              )}
                            </div>
                          </div>
                        </section>
                      </div>
                    </div>
                  </div>
                </section>
              )}
              <div>
                <div data-container="about">
                  {renderEditButton({ text: 'Edit Story', action: () => setEditRichTextModalIsOpen(true) })}
                  {ReactHtmlParser(teamPageInfo.data.richText)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
